import {
    IConnectionPayload,
    IConnectionResponse,
    IInstallation,
    IUser,
    IIntegrationEvent,
    IInstallationSettings,
    ISettingsResponse,
    IVersionUpdate,
} from '../../models';
import { doFetch, generateHeaders } from '../../helpers/fetcher';
import { fetchMethods } from '../../static/constants';

// Events
export const getEvents = async (user: IUser, installation: IInstallation): Promise<IIntegrationEvent[]> =>
    await doFetch(
        `events/${installation.integrationType}/installations/${installation.installationId}`,
        generateHeaders(user)
    );

export const postEvent = async (user: IUser, installation: IInstallation, eventName: string): Promise<void> =>
    installation.installationId
        ? await doFetch(
              `events/${installation.integrationType}/installations/${installation.installationId}`,
              generateHeaders(user),
              fetchMethods.POST,
              {
                  eventName,
              }
          )
        : await doFetch(`events/${installation.integrationType}/public`, generateHeaders(user), fetchMethods.POST, {
              eventName,
          });

// Installation
export const postInstallation = async (user: IUser, installation: IInstallation): Promise<IInstallation> =>
    await doFetch(
        `integrations/${installation.integrationType}/installations`,
        generateHeaders(user),
        fetchMethods.POST
    );

export const getInstallation = async (user: IUser, installation: IInstallation): Promise<IInstallation> => {
    if (installation.installationId) {
        return await doFetch(
            `integrations/${installation.integrationType}/installations/${installation.installationId}`,
            generateHeaders(user),
            fetchMethods.GET
        );
    } else {
        throw new Error('No installationId found');
    }
};

export const deleteInstallation = async (
    user: IUser,
    installation: IInstallation,
    cancelSubscription: boolean
): Promise<void> =>
    await doFetch(
        `integrations/${installation.integrationType}/installations/${installation.installationId}?cancelSubscription=${cancelSubscription}`,
        generateHeaders(user),
        fetchMethods.DELETE
    );

export const updateInstallationVersion = async (user: IUser, installation: IInstallation): Promise<void> =>
    await doFetch(
        `integrations/${installation.integrationType}/installations/${installation.installationId}/version`,
        generateHeaders(user),
        fetchMethods.PUT,
        {
            currentVersion: installation.versionSupport!.currentVersion,
            newVersion: installation.versionSupport!.latestVersion,
        } as IVersionUpdate
    );

// Connection
export const postConnection = async (
    user: IUser,
    installation: IInstallation,
    connectionPayload?: IConnectionPayload
): Promise<IConnectionResponse> => {
    const path = connectionPayload
        ? `integrations/${installation.integrationType}/connection`
        : `integrations/${installation.integrationType}/installations/${installation.installationId}/connection`;
    return await doFetch(path, generateHeaders(user), fetchMethods.POST, connectionPayload);
};

export const deleteConnection = async (user: IUser, installation: IInstallation): Promise<void> =>
    await doFetch(
        `integrations/${installation.integrationType}/installations/${installation.installationId}/connection`,
        generateHeaders(user),
        fetchMethods.DELETE
    );

// Installation Settings
export const getInstallationSettings = async (user: IUser, installation: IInstallation): Promise<ISettingsResponse> => {
    if (installation.installationId) {
        return await doFetch(
            `integrations/${installation.integrationType}/installations/${installation.installationId}/settings`,
            generateHeaders(user),
            fetchMethods.GET
        );
    } else {
        throw Error('No installation Id found');
    }
};

export const postInstallationSettings = async (
    user: IUser,
    installation: IInstallation,
    body: {}
): Promise<ISettingsResponse> =>
    await doFetch(
        `integrations/${installation.integrationType}/installations/${installation.installationId}/settings`,
        generateHeaders(user),
        fetchMethods.POST,
        body
    );

export const putInstallationSettings = async (
    user: IUser,
    installation: IInstallation,
    body: IInstallationSettings | {}
): Promise<void> =>
    await doFetch(
        `integrations/${installation.integrationType}/installations/${installation.installationId}/settings`,
        generateHeaders(user),
        fetchMethods.PUT,
        body
    );

// Free trials
export const putFreeTrial = async (user: IUser, installation: IInstallation): Promise<void> =>
    await doFetch(`freeTrials/${installation.integrationType}`, generateHeaders(user), fetchMethods.PUT);
